"use client"

/* eslint-disable */
import { useState, useEffect } from "react"
import { Table, TableHead, TableRow, TableBody, TableCell, Grid, TablePagination } from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { formatTime } from "../common/util/formatter"
import ReportFilter from "./components/ReportFilter"
import { useTranslation } from "../common/components/LocalizationProvider"
import PageLayout from "../common/components/PageLayout"
import ReportsMenu from "./components/ReportsMenu"
import usePersistedState from "../common/util/usePersistedState"
import ColumnSelect from "./components/ColumnSelect"
import { useCatch } from "../reactHelper"
import useReportStyles from "./common/useReportStyles"
import TableShimmer from "../common/components/TableShimmer"
import { utils, writeFileXLSX } from "xlsx"
import LogoImage from "../login/LogoImage"
import { nativePostMessage } from "../common/components/NativeInterface"
import { sessionActions } from "../store"

// Colunas disponíveis com suas chaves de tradução
const columnsArray = [
  ["eventTime", "costDate"],
  ["deviceId", "sharedDevice"],
  ["name", "sharedName"],
  ["price", "sharedPrice"],
  ["type", "costCategory"],
  ["description", "sharedDescription"],
  ["category", "sharedCategory"],
  ["status", "sharedStatus"],
  ["paymentMethod", "paymentMethod"],
  ["location", "sharedLocation"],
  ["attributes", "atributos"],
]

// Cria o Map
const columnsMap = new Map(columnsArray)

// Componente para cabeçalho de impressão
const PrintHeader = ({ deviceNames, from, to }) => {
  const t = useTranslation();

  return (
    <div style={{ padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <LogoImage
          style={{ height: 60 }}
          onLoad={async () => {
            requestAnimationFrame(() =>
              requestAnimationFrame(() => {
                window.print();
                nativePostMessage(`print|${t('reportCosts')}.pdf`);
              })
            );
          }}
          alt={t('companyLogo')}
        />
        <h1 style={{ fontSize: 24, margin: 0 }}>{t('reportCosts')}</h1>
      </div>
      <div style={{ marginTop: 10, fontSize: 14, color: '#555' }}>
        {deviceNames && deviceNames.length > 0 && (
          <div>
            <strong>{t('sharedDevices')}:</strong> {deviceNames.join(', ')}
          </div>
        )}
        {from && (
          <div>
            <strong>{t('reportFrom')}:</strong> {new Date(from).toLocaleString()}
          </div>
        )}
        {to && (
          <div>
            <strong>{t('reportTo')}:</strong> {new Date(to).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

const CostsReportPage = () => {
  const classes = useReportStyles()
  const t = useTranslation()
  const dispatch = useDispatch()

  // Sem tipagem específica no useSelector
  const devices = useSelector((state) => state.devices.items)
  const printing = useSelector((state) => state.session.printing)

  const [columns, setColumns] = usePersistedState("costsColumns", ["deviceId", "eventTime", "name", "type", "price"])
  const [items, setItems] = useState([])
  const [allItems, setAllItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [availableColumns, setAvailableColumns] = useState([])
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Adicionar evento de afterprint para limpar o estado de impressão
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  // Função segura para tradução que retorna o texto original se a tradução for vazia
  const safeTranslate = (key) => {
    const translated = t(key)
    // Se a tradução for vazia, indefinida, ou igual à chave (não traduzida), retorna a chave original
    return !translated || translated === key || translated.trim() === "" ? key : translated
  }

  // Efeito para traduzir as colunas iniciais
  useEffect(() => {
    // Traduzir as colunas disponíveis usando a função de tradução segura
    const translatedColumns = columnsArray.map(([key, translationKey]) => {
      const translated = safeTranslate(translationKey)
      return [key, translated !== translationKey ? translated : key]
    })
    setAvailableColumns(translatedColumns)
  }, [t])

  // Função para buscar dados e extrair colunas disponíveis
  const fetchData = async (params) => {
    setLoading(true)
    try {
      const { deviceId, deviceIds, from, to, mail, headers } = params

      // Salvar informações para o PDF
      setFromDate(from)
      setToDate(to)
      setSelectedDeviceIds(deviceIds || (deviceId ? [deviceId] : []))

      const query = new URLSearchParams({ from, to, mail })

      // Adicionar os IDs de dispositivo à query
      if (deviceIds && deviceIds.length > 0) {
        deviceIds.forEach(id => query.append("deviceId", id))
      } else if (deviceId) {
        query.append("deviceId", deviceId)
      }

      const response = await fetch(`/api/reports/costs?${query.toString()}`, {
        headers,
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType) {
          if (contentType === "application/json") {
            const data = await response.json()
            setAllItems(data)
            setItems(data)

            // Extrair colunas disponíveis dos dados
            if (data.length > 0) {
              const keySet = new Set()
              data.forEach((item) => {
                Object.keys(item).forEach((key) => {
                  // Adiciona apenas se o valor não for nulo ou vazio
                  if (item[key] !== null && item[key] !== undefined && item[key] !== "") {
                    keySet.add(key)
                  }
                })

                // Adicionar chaves de atributos se existirem
                if (item.attributes) {
                  Object.keys(item.attributes).forEach((key) => {
                    // Adiciona apenas se o valor não for nulo ou vazio
                    if (
                      item.attributes[key] !== null &&
                      item.attributes[key] !== undefined &&
                      item.attributes[key] !== ""
                    ) {
                      keySet.add(key)
                    }
                  })
                }
              })

              // Remover chaves que não queremos mostrar diretamente
              ;["attributes", "id", "__typename"].forEach((key) => keySet.delete(key))

              // Atualizar colunas disponíveis
              const newColumns = [...keySet]
                .map((key) => {
                  // Verificar se a chave existe no mapa de traduções
                  const translationKey = columnsMap.get(key)

                  if (translationKey) {
                    // Usar a tradução segura
                    const translated = safeTranslate(translationKey)
                    return [key, translated !== translationKey ? translated : key]
                  } else {
                    // Se não tiver tradução, usar a chave original
                    return [key, key]
                  }
                })
                .sort((a, b) => a[1].localeCompare(b[1])) // Ordenar por nome traduzido

              // Adicionar a coluna attributes de volta se necessário
              if (data.some((item) => item.attributes && Object.keys(item.attributes).length > 0)) {
                const attributesTranslation = safeTranslate("atributos")
                newColumns.push([
                  "attributes",
                  attributesTranslation !== "atributos" ? attributesTranslation : "attributes",
                ])
              }

              setAvailableColumns(newColumns)
            }
          } else {
            window.location.assign(window.URL.createObjectURL(await response.blob()))
          }
        }
      } else {
        throw Error(await response.text())
      }
    } catch (error) {
      console.error("Erro ao buscar dados:", error)
      // Se você tiver um mecanismo de notificação de erro, use-o aqui
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = useCatch(async (params) => {
    if (params.type === "export") {
      await fetchData(params)
      setTimeout(() => {
        const table = document.querySelector("table")
        writeFileXLSX(utils.table_to_book(table), "relatorio-custos.xlsx")
      }, 500)
    } else if (params.type === "mail") {
      const response = await fetch(`/api/reports/costs/mail`)
      if (!response.ok) {
        throw Error(await response.text())
      }
    } else if (params.type === "pdf") {
      // Buscar os dados primeiro
      await fetchData(params)
      // Ativar modo de impressão
      dispatch(sessionActions.setPrinting(true))
    } else {
      await fetchData(params)
    }
  })

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Formatar atributos para exibição
  const formatarAtributos = (atributos) => {
    if (!atributos || Object.keys(atributos).length === 0) {
      return "-"
    }
    return Object.entries(atributos)
      .map(([chave, valor]) => `${chave}: ${valor}`)
      .join(", ")
  }

  const formatValue = (item, key) => {
    switch (key) {
      case "deviceId":
        return devices[item[key]]?.name || item[key]
      case "eventTime":
        return item[key] ? formatTime(item[key], "DD/MM/YYYY") : null
      case "attributes":
        return formatarAtributos(item[key])
      default:
        // Verificar se é um atributo dentro do objeto attributes
        if (item.attributes && item.attributes[key] !== undefined) {
          return item.attributes[key]
        }
        return item[key]
    }
  }

  // Função para obter o nome de exibição da coluna
  const getColumnDisplayName = (key) => {
    const translationKey = columnsMap.get(key)
    if (translationKey) {
      const translated = safeTranslate(translationKey)
      return translated !== translationKey ? translated : key
    }
    return key
  }

  // Layout para modo de impressão
  if (printing) {
    const deviceNames = selectedDeviceIds.map(id => devices[id]?.name || id).filter(Boolean);

    return (
      <div>
        <PrintHeader
          deviceNames={deviceNames}
          from={fromDate}
          to={toDate}
        />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((key) => (
                  <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={`${item.id || item.deviceId}_${item.eventTime || ''}`}>
                  {columns.map((key) => (
                    <TableCell key={key}>{formatValue(item, key)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  // O conteúdo que será passado como children para o PageLayout
  const pageContent = (
    <>
      <div className={classes.header}>
        <ReportFilter
          handleSubmit={handleSubmit}
          handleSchedule={null}
          showOnly={null}
          ignoreDevice={null}
          multiDevice={true}
          includeGroups={true}
          loading={loading}
          includeExportOptions={true}
        >
          <Grid item xs={12} sm={12}>
            <ColumnSelect
              columns={columns}
              setColumns={setColumns}
              columnsArray={availableColumns}
              rawValues={true}
              disabled={loading}
            />
          </Grid>
        </ReportFilter>
      </div>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((key) => (
              <TableCell key={key}>{getColumnDisplayName(key)}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? (
            items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={`${item.id || item.deviceId}_${new Date().getMilliseconds()}`} hover>
                {columns.map((key) => (
                  <TableCell key={key}>{formatValue(item, key)}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableShimmer columns={columns.length} startAction={false} endAction={false} />
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={items.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />
    </>
  )

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={["reportTitle", "reportCosts"]}>
      {pageContent}
    </PageLayout>
  )
}

export default CostsReportPage